export default class LiquidButton {
  constructor(options = {}) {
    this.tension     = options.tension     || 0.25
    this.width       = options.width       || 200
    this.height      = options.height      || 50
    this.margin      = options.margin      || 50
    this.gap         = options.gap         || 5
    this.forceFactor = options.forceFactor || 0.12
    this.hoverFactor = options.hoverFactor || -0.2
    this.color1      = options.color1      || '#36DFE7'
    this.color2      = options.color2      || '#8F17E1'
    this.color3      = options.color3      || '#E509E6'
    this.layers      = [
      { points: [], viscosity: 0.35, mouseForce: 180, forceLimit: 4 },
      { points: [], viscosity: 0.6, mouseForce: 250, forceLimit: 5 },
    ]
    this.touches = []
    this.canvas  = options.canvas || document.createElement('canvas')
    this.context = this.canvas.getContext('2d')
    this.initOrigins()
    this.animate()
  }

  distance(p1, p2) {
    return Math.sqrt(Math.pow(p1.x - p2.x, 2) + Math.pow(p1.y - p2.y, 2))
  }

  createPoint(x, y) {
    return { x, y, ox: x, oy: y, vx: 0, vy: 0 }
  }

  initOrigins() {
    this.canvas.width  = this.width  + this.margin * 2
    this.canvas.height = this.height + this.margin * 2
    const r     = ~~(this.height / 2)
    const steps = ~~(this.height * 1.25)

    for (let li = 0; li < this.layers.length; li++) {
      const points = []

      for (let x = r; x < this.width - r; x += this.gap)
        points.push(this.createPoint(x + this.margin, this.margin))

      for (let alpha = steps; alpha >= 0; alpha -= this.gap) {
        const angle = (Math.PI / steps) * alpha
        points.push(this.createPoint(
          Math.sin(angle) * r + this.margin + this.width - r,
          Math.cos(angle) * r + this.margin + r
        ))
      }

      for (let x = this.width - r - 1; x >= r; x -= this.gap)
        points.push(this.createPoint(x + this.margin, this.margin + this.height))

      for (let alpha = 0; alpha <= steps; alpha += this.gap) {
        const angle = (Math.PI / steps) * alpha
        points.push(this.createPoint(
          (this.height - Math.sin(angle) * r) + this.margin - r,
          Math.cos(angle) * r + this.margin + r
        ))
      }

      this.layers[li].points = points
    }
  }

  update() {
    for (let li = 0; li < this.layers.length; li++) {
      const layer  = this.layers[li]
      const points = layer.points

      for (let pi = 0; pi < points.length; pi++) {
        const point = points[pi]
        const dx = point.ox - point.x
        const dy = point.oy - point.y
        const d  = Math.sqrt(dx * dx + dy * dy)
        const f  = d * this.forceFactor
        point.vx += f * ((dx / d) || 0)
        point.vy += f * ((dy / d) || 0)

        for (let ti = 0; ti < this.touches.length; ti++) {
          const touch = this.touches[ti]
          let mouseForce = layer.mouseForce
          if (
            touch.x > this.margin && touch.x < this.margin + this.width &&
            touch.y > this.margin && touch.y < this.margin + this.height
          ) mouseForce *= -this.hoverFactor

          const mx = point.x - touch.x
          const my = point.y - touch.y
          const md = Math.sqrt(mx * mx + my * my)
          const mf = Math.max(-layer.forceLimit, Math.min(layer.forceLimit, (mouseForce * touch.force) / md))
          point.vx += mf * ((mx / md) || 0)
          point.vy += mf * ((my / md) || 0)
        }

        point.vx *= layer.viscosity
        point.vy *= layer.viscosity
        point.x  += point.vx
        point.y  += point.vy
      }

      for (let pi = 0; pi < points.length; pi++) {
        const prev  = points[(pi + points.length - 1) % points.length]
        const point = points[pi]
        const next  = points[(pi + 1) % points.length]
        const dPrev = this.distance(point, prev)
        const dNext = this.distance(point, next)
        const line  = { x: next.x - prev.x, y: next.y - prev.y }
        const dLine = Math.sqrt(line.x * line.x + line.y * line.y)
        point.cPrev = { x: point.x - (line.x / dLine) * dPrev * this.tension, y: point.y - (line.y / dLine) * dPrev * this.tension }
        point.cNext = { x: point.x + (line.x / dLine) * dNext * this.tension, y: point.y + (line.y / dLine) * dNext * this.tension }
      }
    }
  }

  draw() {
    // Fade in/out : le canvas n'est visible qu'au hover
    if (this.touches.length > 0) this.alpha = Math.min(1, (this.alpha || 0) + 0.08)
    else this.alpha = Math.max(0, (this.alpha || 0) - 0.08)

    this.context.clearRect(0, 0, this.canvas.width, this.canvas.height)
    if (this.alpha <= 0) return
    this.context.globalAlpha = this.alpha

    for (let li = 0; li < this.layers.length; li++) {
      const layer  = this.layers[li]
      const points = layer.points

      if (li === 1) {
        if (this.touches.length > 0) {
          const gx   = this.touches[0].x
          const gy   = this.touches[0].y
          layer.color = this.context.createRadialGradient(gx, gy, this.height * 2, gx, gy, 0)
          layer.color.addColorStop(0, this.color2)
          layer.color.addColorStop(1, this.color3)
        } else {
          layer.color = this.color2
        }
      } else {
        layer.color = this.color1
      }

      this.context.fillStyle = layer.color
      this.context.beginPath()
      this.context.moveTo(points[0].x, points[0].y)
      for (let pi = 1; pi < points.length; pi++) {
        this.context.bezierCurveTo(
          points[pi % points.length].cNext.x,
          points[pi % points.length].cNext.y,
          points[(pi + 1) % points.length].cPrev.x,
          points[(pi + 1) % points.length].cPrev.y,
          points[(pi + 1) % points.length].x,
          points[(pi + 1) % points.length].y
        )
      }
      this.context.fill()
    }
  }

  animate() {
    requestAnimationFrame(() => this.animate())
    this.update()
    this.draw()
  }
}
